import { forwardRef, useMemo } from "react";

import { Box } from "@mui/material";
import dynamic from "next/dynamic";

import { FormButton, FormButtonProps } from "@/forms-design-system/FormButton";
import { useFetchHeartsAnimation } from "@/forms-design-system/HeartsFormButton/useFetchHeartsAnimation";

const Lottie = dynamic(() => import("react-lottie"), { ssr: false });

export type HeartsFormButtonProps = Omit<FormButtonProps, "variant" | "size" | "fullWidth"> & {
  autoplay?: boolean;
  animated?: boolean;
  animationKey?: string | number;
  loop?: boolean;
};

export const HeartsFormButton = forwardRef<HTMLButtonElement, HeartsFormButtonProps>(
  (
    { animated = false, animationKey = 0, loop = false, disabled = false, children, autoplay = false, ...rest },
    ref
  ) => {
    const heartsAnimationData = useFetchHeartsAnimation();
    const isCypressTest = typeof window !== "undefined" && !!window?.Cypress;

    const lottieOptions = useMemo(() => {
      return {
        animationData: heartsAnimationData,
        autoplay,
        loop,
        rendererSettings: {
          preserveAspectRatio: "xMidYMid slice",
        },
      };
    }, [heartsAnimationData, autoplay, loop]);

    const heartsAnimation = (
      <>
        {animated && heartsAnimationData && !disabled && !isCypressTest && (
          <Box
            key={`${animationKey}-${loop}`}
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              zIndex: 0,
              width: "100%",
              height: "100%",
              pointerEvents: "none",
              opacity: 0.5,
            }}
          >
            <Lottie options={lottieOptions} width="100%" height="100%" speed={0.7} isClickToPauseDisabled />
          </Box>
        )}
      </>
    );

    return (
      <FormButton
        {...rest}
        ref={ref}
        variant="solid"
        size="large"
        fullWidth
        disabled={disabled}
        backgroundContent={heartsAnimation}
      >
        {children}
      </FormButton>
    );
  }
);

HeartsFormButton.displayName = "HeartsFormButton";
